import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { InputTextModule } from "primeng/inputtext";
import { ButtonComponent } from "../../@core/components/ui/button/button.component";
import { AuthenticationService, EMAIL_TYPE } from "../../@core/services/authentication.service";
import { ToastService } from "../../@core/services/toast.service";
import { TextCasePipe } from "../../@shared/pipes/textCase.pipe";
import { CaseType } from "src/app/@shared/services/case-format.service";
import { IOrganization } from "src/app/@core/models/model";
import { AssetUrlPipe } from "../../@shared/pipes/asset-url.pipe";


@Component({
  selector: 'app-sign-up',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    InputTextModule,
    ButtonComponent,
    TextCasePipe,
    AssetUrlPipe
  ],
  templateUrl: './sign-up.component.html',
  styleUrl: './sign-up.component.scss'
})
export class SignUpComponent implements OnInit {

  signupForm!: FormGroup;

  defaultUrl: string = ''
  returnUrl: string = this.defaultUrl;
  email: string = ''
  organizationExists = false;
  organizationWithSameDomain: IOrganization | null = null;
  joinRequestLoading = false;
  joinRequestSuccess = false;

  CASE_TYPE = CaseType;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private toastService: ToastService,
    private authenticationService: AuthenticationService
  ) {
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || this.defaultUrl;
  }

  ngOnInit() {

    this.signupForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });
  }

  onSubmit() {
    if (this.signupForm.valid) {
      const credentials = this.signupForm.value;
      this.organizationExists = false;
      this.joinRequestSuccess = false;
      this.signupForm.get('email')?.enable();
      this.authenticationService.logout(false);
      this.authenticationService.verifyEmailForSignUp(credentials.email).subscribe((res) => {
        if (res.success) {
          const data = res.data;
          if (data.emailType === EMAIL_TYPE.DUPLICATE) {
            this.toastService.error('Email already exists. Please login to continue.');
            this.router.navigate(['/login']);
            return;
          }
          if (data.emailType === EMAIL_TYPE.PERSONAL) {
            this.toastService.error('Personal email is not allowed for sign up');
            this.signupForm.reset();
            this.signupForm.get('email')?.enable();
          }
          else if (data.emailType === EMAIL_TYPE.INVALID) {
            this.toastService.error('Invalid email');
            this.signupForm.reset();
            this.signupForm.get('email')?.enable();
          }
          else if (data.emailType === EMAIL_TYPE.WORK) {
            if (data.organizationWithSameDomain) {
              this.organizationExists = true;
              this.organizationWithSameDomain = data.organizationWithSameDomain;
              this.signupForm.get('email')?.disable();
              this.toastService.info('Organization with the same domain exists. You can request to join.', 'Info');
              return;
            }
            else {
              this.authenticationService.sendOTP(credentials.email).subscribe((res) => {
                if (res.success) {
                  this.toastService.success(res.message);
                  this.router.navigate(['/verify/' + credentials.email], { queryParams: { domain: data.domain } })
                }
                else {
                  this.toastService.error(res.message);
                  this.router.navigate(['/signup'])
                }
              })
            }
          }
        }
      })

    }
  }

  requestToJoinExistingOrg() {
    const emailControl = this.signupForm.get('email');
    if (!emailControl?.value || !this.organizationWithSameDomain) {
      this.signupForm.markAllAsTouched();
      return;
    }

    this.joinRequestLoading = true;
    const email = emailControl.value;
    this.authenticationService.requestToJoinOrganization(email, this.organizationWithSameDomain?._id).subscribe((res) => {
      if (res?.success) {
        this.joinRequestLoading = false;
        this.joinRequestSuccess = true;
        this.toastService.success('Request sent. We will notify you once approved.');
      } else {
        this.joinRequestLoading = false;
        this.toastService.error(res?.message || 'Unable to send join request');
      }
    });
  }

  cancelJoinRequest() {
    this.signupForm.reset();
    this.organizationExists = false;
    this.signupForm.get('email')?.enable();
  }

}
